import { useDebounceEffect } from 'ahooks';
import { RefObject, useRef } from 'react';

export interface UseInfiniteScrollRenderParams<T> {
  currentList?: T[];
  hasNextPage?: boolean;
  isRequesting?: boolean;
  getNextPage: () => void;
}

export interface UseInfiniteScrollRenderResult {
  containerRef: RefObject<HTMLDivElement>;
  wrapperRef: RefObject<HTMLDivElement>;
}

export function useInfiniteScrollRender<T = any>(
  { hasNextPage, isRequesting, currentList, getNextPage }: UseInfiniteScrollRenderParams<T>,
  deps?: any[],
): UseInfiniteScrollRenderResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useDebounceEffect(
    () => {
      const render = () => {
        if (isRequesting || !hasNextPage) return;

        const visibleHeight = containerRef?.current?.clientHeight || 0;
        const totalHeight = wrapperRef?.current?.clientHeight || 0;

        if (totalHeight < visibleHeight) {
          getNextPage?.();
        }
      };

      render();

      window.addEventListener('resize', render);

      return () => {
        window.removeEventListener('resize', render);
      };
    },
    [containerRef, wrapperRef, currentList?.length, ...(deps || [])],
    { wait: 10 },
  );

  return { containerRef, wrapperRef };
}
